'use client';

import { useState } from 'react';
import { BellRing, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '#/components/ui/button';
import { usePushSubscription } from '#/hooks/use-push-subscription';

/**
 * 비로그인 방문자용 알림 구독 카드 — baikal/rnd/bareunai#264.
 *
 * **가입하지 않아도 구독된다.** 구독은 원래 브라우저 단위라 계정이 필요 없고,
 * 같은 브라우저로 다시 오면 그대로 알림을 받는다. 나중에 로그인하면 같은 endpoint 로
 * 재등록되어 그 구독이 회원 것으로 승격된다.
 *
 * 원칙:
 *  - 페이지 로드 즉시 권한을 요구하지 않는다. 버튼을 누른 그 순간에만 요청한다(soft-ask).
 *  - 미지원 브라우저에서는 **아무것도 렌더하지 않는다.**
 *
 * 비로그인 구독자가 받는 알림은 공지성 2종(새 소식·문서 업데이트)뿐이다.
 * 제보·결제·잔량처럼 «누구의» 사건인지 알아야 하는 알림은 계정이 있어야 성립한다.
 */
export function PushOptInCard({
  title = '새 소식을 알림으로 받아 보세요',
  description = '가입하지 않아도 됩니다. 이 브라우저로 다시 오시면 새 기능·문서 업데이트를 알려드립니다.',
  onSubscribed,
  onDismiss
}: {
  title?: string;
  description?: string;
  /** 구독 성공을 바깥에 알린다(권유 기록 등). */
  onSubscribed?: () => void;
  /** 주면 닫기(X) 버튼이 생긴다. 상시 노출되는 자리에서는 주지 않는다. */
  onDismiss?: () => void;
}) {
  const { supported, ready, permission, subscribed, busy, subscribe } =
    usePushSubscription();
  const [justSubscribed, setJustSubscribed] = useState(false);

  // 지원 여부 판정 전 / 미지원 / 차단됨 → 노출하지 않는다.
  if (!ready || !supported || permission === 'denied') return null;

  // 이미 구독 중이면 굳이 다시 권하지 않는다.
  if (subscribed && !justSubscribed) return null;

  if (justSubscribed) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
        <Check className="h-4 w-4 shrink-0 text-primary" />
        <span>알림을 켰습니다. 새 소식이 생기면 알려드릴게요.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <BellRing className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          size="sm"
          disabled={busy}
          onClick={async () => {
            const ok = await subscribe();
            if (ok) {
              setJustSubscribed(true);
              onSubscribed?.();
            } else toast.error('알림을 켜지 못했습니다.');
          }}
        >
          알림 받기
        </Button>
        {onDismiss && (
          <Button
            size="sm"
            variant="ghost"
            aria-label="알림 안내 닫기"
            onClick={onDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
